import api from "@/request/services";
import {
  resolveNotificationErrorMessage,
  isNotificationResponseSuccessful,
} from "./notificationServiceUtils";
import { getNotificationAuthHeaders } from "./notificationAuthHeaders";

const INBOX_LIST_ENDPOINT = "/api/notifications/inbox";
const INBOX_UNREAD_COUNT_ENDPOINT = "/api/notifications/inbox/unread-count";
const INBOX_MARK_ALL_READ_ENDPOINT = "/api/notifications/inbox/read-all";
const INBOX_BROADCAST_ENDPOINT = "/api/notifications/broadcast";
const INBOX_BROADCAST_PUSH_ENDPOINT = "/api/notifications/broadcast/push";
const USER_LIST_ENDPOINT = "get_user_list";
const CUSTOMER_LIST_ENDPOINT = "get_customer";
const DEFAULT_RECIPIENT_PAGE_SIZE = 300;
const MAX_RECIPIENT_PAGES = 40;
const PREVIEW_RECIPIENT_LIMIT = 8;

const toCommaSeparated = (value) => {
  if (!Array.isArray(value)) return value;
  const arr = value.map((item) => `${item || ""}`.trim()).filter(Boolean);
  return arr.length ? arr.join(",") : undefined;
};

const callGet = async (endpoint = "", params = {}) => {
  const headers = getNotificationAuthHeaders();
  const response = await api.getRequest({
    url: endpoint,
    params,
    headers,
  });
  if (isNotificationResponseSuccessful(response)) {
    return response;
  }
  throw new Error(
    `${endpoint}: ${resolveNotificationErrorMessage(response)}`
  );
};

const callPost = async (endpoint = "", payload = {}) => {
  const headers = getNotificationAuthHeaders();
  const response = await api.list({
    listURL: endpoint,
    options: payload,
    headers,
  });
  if (isNotificationResponseSuccessful(response)) {
    return response;
  }
  throw new Error(
    `${endpoint}: ${resolveNotificationErrorMessage(response)}`
  );
};

const toInt = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const unwrapPayload = (response = {}) => {
  if (!response || typeof response !== "object") return {};
  if (response.body && typeof response.body === "object" && !Array.isArray(response.body)) {
    return response.body;
  }
  return response;
};

const normalizeNotificationListResponse = (response = {}) => {
  const payload = unwrapPayload(response);
  const items = Array.isArray(payload.items)
    ? payload.items
    : Array.isArray(payload.arrList)
      ? payload.arrList
      : [];
  const nextCursor =
    `${payload.nextCursor || payload?.pagination?.nextCursor || ""}`.trim() || null;

  return {
    ...payload,
    items,
    arrList: items,
    nextCursor,
  };
};

const listNotificationInbox = async ({
  limit = 20,
  cursor,
  category,
  search,
  unreadOnly,
  priority,
} = {}) => {
  const params = {
    ...(limit ? { limit } : {}),
    ...(cursor ? { cursor } : {}),
    ...(toCommaSeparated(category) ? { category: toCommaSeparated(category) } : {}),
    ...(toCommaSeparated(priority) ? { priority: toCommaSeparated(priority) } : {}),
    ...(search ? { search } : {}),
    ...(typeof unreadOnly === "boolean" ? { unreadOnly } : {}),
  };

  const response = await callGet(INBOX_LIST_ENDPOINT, params);
  return normalizeNotificationListResponse(response);
};

const getNotificationInboxUnreadCount = async ({
  category,
  priority,
  search,
} = {}) => {
  const params = {
    ...(toCommaSeparated(category) ? { category: toCommaSeparated(category) } : {}),
    ...(toCommaSeparated(priority) ? { priority: toCommaSeparated(priority) } : {}),
    ...(search ? { search } : {}),
  };
  const response = await callGet(INBOX_UNREAD_COUNT_ENDPOINT, params);
  const payload = unwrapPayload(response);
  const count = Number(payload?.count ?? payload?.unreadCount ?? 0);
  return {
    ...payload,
    count: Number.isFinite(count) ? count : 0,
  };
};

const markNotificationInboxAsRead = async (notificationId) => {
  if (!notificationId) {
    throw new Error("notificationId is required");
  }
  const encodedId = encodeURIComponent(String(notificationId));
  return callPost(`/api/notifications/inbox/${encodedId}/read`, {});
};

const markAllNotificationInboxAsRead = async ({
  category,
  priority,
  search,
} = {}) => {
  const payload = {
    ...(toCommaSeparated(category) ? { category: toCommaSeparated(category) } : {}),
    ...(toCommaSeparated(priority) ? { priority: toCommaSeparated(priority) } : {}),
    ...(search ? { search } : {}),
  };
  return callPost(INBOX_MARK_ALL_READ_ENDPOINT, payload);
};

const createNotificationBroadcast = async ({
  audienceType,
  title,
  content,
  category,
  priority,
  deepLink,
  metadata,
  notificationModes = ["INBOX_ONLY"],
} = {}) => {
  const payload = {
    ...(audienceType ? { audienceType } : {}),
    ...(title ? { title } : {}),
    ...(content ? { content } : {}),
    ...(category ? { category } : {}),
    ...(priority ? { priority } : {}),
    ...(deepLink ? { deepLink } : {}),
    ...(metadata ? { metadata } : {}),
    ...(Array.isArray(notificationModes) && notificationModes.length
      ? { notificationModes }
      : {}),
  };

  return callPost(INBOX_BROADCAST_ENDPOINT, payload);
};

const normalizeAudienceType = (value = "") => `${value || ""}`.trim().toUpperCase();
const safeTrim = (value = "") => `${value || ""}`.trim();
const toUpper = (value = "") => safeTrim(value).toUpperCase();

const normalizeStatusFilter = (value = "") => {
  const normalized = toUpper(value);
  if (normalized === "ACTIVE") return "N";
  if (normalized === "INACTIVE") return "B";
  return "";
};

const resolveAudienceListURL = (audienceType = "CUSTOMER") => {
  const normalizedAudience = normalizeAudienceType(audienceType);
  return normalizedAudience === "EMPLOYEE"
    ? USER_LIST_ENDPOINT
    : CUSTOMER_LIST_ENDPOINT;
};

const extractTokenArray = (entry = {}) => {
  const arrCandidates = [
    entry?.token,
    entry?.fcmToken,
    entry?.strDeviceToken,
    entry?.strFcmToken,
    ...(Array.isArray(entry?.arrDeviceTokens) ? entry.arrDeviceTokens : []),
    ...(Array.isArray(entry?.arrFcmTokens) ? entry.arrFcmTokens : []),
    ...(Array.isArray(entry?.deviceTokens) ? entry.deviceTokens : []),
    ...(Array.isArray(entry?.fcmTokens) ? entry.fcmTokens : []),
  ];
  return Array.from(
    new Set(arrCandidates.map((value) => safeTrim(value)).filter(Boolean))
  );
};

const normalizeRecipient = (entry = {}) => {
  const strFirstName = safeTrim(entry?.strFirstName);
  const strMiddleName = safeTrim(entry?.strMiddleName);
  const strLastName = safeTrim(entry?.strLastName);
  const strComposedName = [strFirstName, strMiddleName, strLastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    id:
      safeTrim(entry?._id) ||
      safeTrim(entry?.strCustomerId) ||
      safeTrim(entry?.strUserId) ||
      safeTrim(entry?.id),
    email: safeTrim(entry?.strEmail) || safeTrim(entry?.email),
    name:
      safeTrim(entry?.strName) ||
      strComposedName ||
      safeTrim(entry?.name) ||
      safeTrim(entry?.strMobileNo) ||
      "Unknown",
    role:
      toUpper(entry?.strUserRole) ||
      toUpper(entry?.role) ||
      toUpper(entry?.strRoleName) ||
      toUpper(entry?.type),
    status: toUpper(entry?.chrStatus || entry?.strStatus || entry?.status),
    city: safeTrim(entry?.strCity) || safeTrim(entry?.city) || safeTrim(entry?.strDistrict),
    branchId:
      safeTrim(entry?.strBranchId) ||
      safeTrim(entry?.branchId) ||
      safeTrim(entry?.strOfficeBranchId) ||
      safeTrim(entry?.strOfficeId),
    tokens: extractTokenArray(entry),
    raw: entry,
  };
};

const applyClientRecipientFilters = (arrRecipients = [], filters = {}) => {
  const arrRoles = Array.isArray(filters?.roles)
    ? filters.roles.map((value) => toUpper(value)).filter(Boolean)
    : [];
  const arrCities = Array.isArray(filters?.cities)
    ? filters.cities.map((value) => toUpper(value)).filter(Boolean)
    : [];
  const arrBranchIds = Array.isArray(filters?.branchIds)
    ? filters.branchIds.map((value) => safeTrim(value)).filter(Boolean)
    : [];
  const chrStatus = normalizeStatusFilter(filters?.status || "ACTIVE");

  return arrRecipients.filter((recipient = {}) => {
    if (chrStatus && toUpper(recipient?.status) !== chrStatus) return false;
    if (arrRoles.length && !arrRoles.includes(toUpper(recipient?.role))) return false;
    if (arrCities.length && !arrCities.includes(toUpper(recipient?.city))) return false;
    if (
      arrBranchIds.length &&
      !arrBranchIds.includes(safeTrim(recipient?.branchId))
    ) {
      return false;
    }
    return true;
  });
};

const fetchBroadcastRecipientPage = async ({
  audienceType = "CUSTOMER",
  page = 1,
  pageSize = DEFAULT_RECIPIENT_PAGE_SIZE,
} = {}) => {
  const listURL = resolveAudienceListURL(audienceType);

  const headers = getNotificationAuthHeaders();
  const response = await api.list({
    listURL,
    options: {
      page: Number(page) || 1,
      pageSize: Number(pageSize) || DEFAULT_RECIPIENT_PAGE_SIZE,
    },
    headers,
  });
  if (!isNotificationResponseSuccessful(response)) {
    throw new Error(
      `${listURL}: ${resolveNotificationErrorMessage(response)}`
    );
  }

  const arrItems = Array.isArray(response?.arrList) ? response.arrList : [];
  const objPagination = response?.pagination || {};

  return {
    arrItems,
    objPagination: {
      page: Number(objPagination?.page) || Number(page) || 1,
      pageSize: Number(objPagination?.pageSize) || Number(pageSize) || DEFAULT_RECIPIENT_PAGE_SIZE,
      count: Number(objPagination?.count) || 0,
    },
  };
};

const listBroadcastAudienceCandidates = async (input = "CUSTOMER") => {
  const objInput =
    typeof input === "string"
      ? { audienceType: input }
      : input && typeof input === "object"
        ? input
        : {};
  const response = await fetchBroadcastRecipientPage({
    audienceType: objInput?.audienceType || "CUSTOMER",
    page: objInput?.page || 1,
    pageSize: objInput?.pageSize || DEFAULT_RECIPIENT_PAGE_SIZE,
  });
  return response.arrItems.map((entry) => normalizeRecipient(entry));
};

const resolveBroadcastRecipients = async ({
  audienceType = "CUSTOMER",
  filters = {},
  pageSize = DEFAULT_RECIPIENT_PAGE_SIZE,
  maxPages = MAX_RECIPIENT_PAGES,
  onProgress,
} = {}) => {
  const arrAllRecipients = [];
  let page = 1;
  let countHint = 0;

  while (page <= maxPages) {
    const response = await fetchBroadcastRecipientPage({
      audienceType,
      page,
      pageSize,
    });
    const arrCurrent = response.arrItems.map((entry) => normalizeRecipient(entry));
    arrAllRecipients.push(...arrCurrent);

    countHint = Number(response?.objPagination?.count) || countHint;
    if (typeof onProgress === "function") {
      onProgress({
        page,
        fetchedCount: arrAllRecipients.length,
        totalHint: countHint || null,
      });
    }

    const intCurrentPage = Number(response?.objPagination?.page) || page;
    const intPageSize = Number(response?.objPagination?.pageSize) || pageSize;
    const intTotalCount = Number(response?.objPagination?.count) || 0;
    const hasNextByCount =
      intTotalCount > 0 && intCurrentPage * intPageSize < intTotalCount;
    const hasNextByPageSize = response.arrItems.length >= intPageSize;
    if (!(hasNextByCount || hasNextByPageSize)) {
      break;
    }
    page += 1;
  }

  const arrFiltered = applyClientRecipientFilters(arrAllRecipients, filters);
  const recipientIds = arrFiltered.map((item) => item.id).filter(Boolean);
  const recipientEmails = arrFiltered.map((item) => item.email).filter(Boolean);
  const recipientTokens = Array.from(
    new Set(
      arrFiltered
        .flatMap((item) => (Array.isArray(item.tokens) ? item.tokens : []))
        .map((value) => safeTrim(value))
        .filter(Boolean)
    )
  );

  return {
    audienceType: normalizeAudienceType(audienceType),
    fetchedCount: arrAllRecipients.length,
    filteredCount: arrFiltered.length,
    recipientIds,
    recipientEmails,
    recipientTokens,
    previewRecipients: arrFiltered.slice(0, PREVIEW_RECIPIENT_LIMIT),
  };
};

const broadcastPushNotification = async ({
  audienceType,
  title,
  body,
  filters = {},
  dataPayload = {},
  resolvedRecipients = {},
  category = "GENERAL",
  priority = "NORMAL",
} = {}) => {
  const normalizedAudience = normalizeAudienceType(audienceType);
  if (!normalizedAudience) {
    throw new Error("Audience is required");
  }

  const normalizedTitle = `${title || ""}`.trim();
  const normalizedBody = `${body || ""}`.trim();
  if (!normalizedTitle) {
    throw new Error("Title is required");
  }
  if (!normalizedBody) {
    throw new Error("Body is required");
  }

  const normalizedRoles = Array.isArray(filters?.roles)
    ? filters.roles.map((value) => `${value || ""}`.trim()).filter(Boolean)
    : [];
  const normalizedBranches = Array.isArray(filters?.branchIds)
    ? filters.branchIds.map((value) => `${value || ""}`.trim()).filter(Boolean)
    : [];
  const normalizedCities = Array.isArray(filters?.cities)
    ? filters.cities.map((value) => `${value || ""}`.trim()).filter(Boolean)
    : [];
  const normalizedStatus = `${filters?.status || "ACTIVE"}`.trim().toUpperCase();
  const chrStatus = normalizeStatusFilter(normalizedStatus);

  const payloadEntries = Object.entries(dataPayload || {}).reduce(
    (acc, [key, value]) => {
      const normalizedKey = `${key || ""}`.trim();
      const normalizedValue = `${value ?? ""}`.trim();
      if (!normalizedKey || !normalizedValue) return acc;
      acc[normalizedKey] = normalizedValue;
      return acc;
    },
    {}
  );

  const deepLink = {
    ...(payloadEntries.page ? { screen: payloadEntries.page } : {}),
    ...(payloadEntries.bookingId ? { bookingId: payloadEntries.bookingId } : {}),
    ...(payloadEntries.reservationId
      ? { reservationId: payloadEntries.reservationId }
      : {}),
    ...(payloadEntries.taskId ? { taskId: payloadEntries.taskId } : {}),
    ...(payloadEntries.paymentId ? { paymentId: payloadEntries.paymentId } : {}),
    ...(payloadEntries.id ? { notificationId: payloadEntries.id } : {}),
  };

  const metadata = {
    sourceApp: "admin",
    body: normalizedBody,
    dataPayload: payloadEntries,
    filters: {
      roles: normalizedRoles,
      status: normalizedStatus,
      chrStatus: chrStatus || undefined,
      cities: normalizedCities,
      branchIds: normalizedBranches,
    },
    recipientResolution: {
      count: Number(resolvedRecipients?.filteredCount || 0),
      recipientIds: Array.isArray(resolvedRecipients?.recipientIds)
        ? resolvedRecipients.recipientIds
        : [],
      recipientEmails: Array.isArray(resolvedRecipients?.recipientEmails)
        ? resolvedRecipients.recipientEmails
        : [],
      recipientTokens: Array.isArray(resolvedRecipients?.recipientTokens)
        ? resolvedRecipients.recipientTokens
        : [],
      previewRecipients: Array.isArray(resolvedRecipients?.previewRecipients)
        ? resolvedRecipients.previewRecipients
        : [],
    },
  };

  const payload = {
    audienceType: normalizedAudience,
    title: normalizedTitle,
    content: normalizedBody,
    category: `${category || "GENERAL"}`.trim().toUpperCase(),
    priority: `${priority || "NORMAL"}`.trim().toUpperCase(),
    notificationModes: ["PUSH_FCM"],
    ...(Object.keys(deepLink).length ? { deepLink } : {}),
    ...(Object.keys(metadata).length ? { metadata } : {}),
    ...(normalizedRoles.length ? { targetRoleIds: normalizedRoles } : {}),
    ...(normalizedBranches.length ? { targetBranchIds: normalizedBranches } : {}),
  };

  return callPost(INBOX_BROADCAST_ENDPOINT, payload);
};

const buildNormalizedBroadcastError = ({
  error,
  fallbackMessage = "Broadcast push failed",
} = {}) => {
  const rawMessage = `${error?.message || fallbackMessage}`.trim();
  const statusCode = toInt(error?.statusCode, 0);
  const isTimeout =
    rawMessage.toLowerCase().includes("timeout") ||
    rawMessage.toLowerCase().includes("network");

  return {
    code:
      error?.code ||
      (statusCode === 401
        ? "UNAUTHORIZED"
        : statusCode === 403
          ? "FORBIDDEN"
          : statusCode === 404
            ? "NOT_FOUND"
            : isTimeout
              ? "NETWORK_ERROR"
              : "BROADCAST_PUSH_FAILED"),
    message: rawMessage || fallbackMessage,
    statusCode: statusCode || null,
    details: error?.details || null,
    retriable: isTimeout || statusCode >= 500,
  };
};

const shouldFallbackToLegacyBroadcast = (error = {}) => {
  const message = `${error?.message || ""}`.toLowerCase();
  return (
    message.includes("not found") ||
    message.includes("cannot post") ||
    message.includes("404") ||
    message.includes("route")
  );
};

const classifyBroadcastState = ({
  sent = 0,
  failed = 0,
  skipped = 0,
  requestedCount = 0,
} = {}) => {
  if (sent > 0 && (failed > 0 || skipped > 0)) {
    return "partial-failure";
  }
  if (sent <= 0 && (failed > 0 || skipped > 0 || requestedCount > 0)) {
    return "failure";
  }
  return "success";
};

const normalizeBroadcastResponse = ({
  response = {},
  requestedCount = 0,
  endpointUsed = INBOX_BROADCAST_ENDPOINT,
} = {}) => {
  const payload = unwrapPayload(response);
  const push = payload?.push || response?.push || {};

  const sent = toInt(push?.sent, 0);
  const failed = toInt(push?.failed, 0);
  const skipped = toInt(push?.skipped, 0);
  const hasPushStats = sent > 0 || failed > 0 || skipped > 0;
  const fallbackSent = requestedCount > 0 ? requestedCount : 0;

  const summary = {
    endpointUsed,
    broadcastId:
      `${payload?.broadcastId || payload?.notification?._id || response?.notification?._id || ""}`.trim() ||
      null,
    message:
      `${payload?.strMessage || payload?.message || response?.strMessage || "Broadcast queued"}`.trim(),
    requestedCount: toInt(
      payload?.recipientCount || response?.recipientCount || requestedCount,
      requestedCount
    ),
    sent: hasPushStats ? sent : fallbackSent,
    failed,
    skipped,
  };

  return {
    ok: true,
    state: classifyBroadcastState(summary),
    summary,
    error: null,
    raw: response,
  };
};

const buildPushBroadcastPayload = ({
  audienceType,
  title,
  body,
  filters = {},
  dataPayload = {},
  resolvedRecipients = {},
  category = "GENERAL",
  priority = "NORMAL",
} = {}) => {
  const normalizedAudience = normalizeAudienceType(audienceType);
  if (!normalizedAudience) {
    throw new Error("Audience is required");
  }

  const normalizedTitle = `${title || ""}`.trim();
  const normalizedBody = `${body || ""}`.trim();
  if (!normalizedTitle) {
    throw new Error("Title is required");
  }
  if (!normalizedBody) {
    throw new Error("Body is required");
  }

  const normalizedRoles = Array.isArray(filters?.roles)
    ? filters.roles.map((value) => `${value || ""}`.trim()).filter(Boolean)
    : [];
  const normalizedBranches = Array.isArray(filters?.branchIds)
    ? filters.branchIds.map((value) => `${value || ""}`.trim()).filter(Boolean)
    : [];
  const normalizedCities = Array.isArray(filters?.cities)
    ? filters.cities.map((value) => `${value || ""}`.trim()).filter(Boolean)
    : [];
  const normalizedStatus = `${filters?.status || "ACTIVE"}`.trim().toUpperCase();
  const chrStatus = normalizeStatusFilter(normalizedStatus);

  const payloadEntries = Object.entries(dataPayload || {}).reduce(
    (acc, [key, value]) => {
      const normalizedKey = `${key || ""}`.trim();
      const normalizedValue = `${value ?? ""}`.trim();
      if (!normalizedKey || !normalizedValue) return acc;
      acc[normalizedKey] = normalizedValue;
      return acc;
    },
    {}
  );

  const deepLink = {
    ...(payloadEntries.page ? { screen: payloadEntries.page } : {}),
    ...(payloadEntries.bookingId ? { bookingId: payloadEntries.bookingId } : {}),
    ...(payloadEntries.reservationId
      ? { reservationId: payloadEntries.reservationId }
      : {}),
    ...(payloadEntries.taskId ? { taskId: payloadEntries.taskId } : {}),
    ...(payloadEntries.paymentId ? { paymentId: payloadEntries.paymentId } : {}),
    ...(payloadEntries.id ? { notificationId: payloadEntries.id } : {}),
  };

  const metadata = {
    sourceApp: "admin",
    body: normalizedBody,
    dataPayload: payloadEntries,
    filters: {
      roles: normalizedRoles,
      status: normalizedStatus,
      chrStatus: chrStatus || undefined,
      cities: normalizedCities,
      branchIds: normalizedBranches,
    },
    recipientResolution: {
      count: Number(resolvedRecipients?.filteredCount || 0),
      recipientIds: Array.isArray(resolvedRecipients?.recipientIds)
        ? resolvedRecipients.recipientIds
        : [],
      recipientEmails: Array.isArray(resolvedRecipients?.recipientEmails)
        ? resolvedRecipients.recipientEmails
        : [],
      recipientTokens: Array.isArray(resolvedRecipients?.recipientTokens)
        ? resolvedRecipients.recipientTokens
        : [],
      previewRecipients: Array.isArray(resolvedRecipients?.previewRecipients)
        ? resolvedRecipients.previewRecipients
        : [],
    },
  };

  return {
    audienceType: normalizedAudience,
    title: normalizedTitle,
    content: normalizedBody,
    category: `${category || "GENERAL"}`.trim().toUpperCase(),
    priority: `${priority || "NORMAL"}`.trim().toUpperCase(),
    notificationModes: ["PUSH_FCM"],
    ...(Object.keys(deepLink).length ? { deepLink } : {}),
    ...(Object.keys(metadata).length ? { metadata } : {}),
    ...(normalizedRoles.length ? { targetRoleIds: normalizedRoles } : {}),
    ...(normalizedBranches.length ? { targetBranchIds: normalizedBranches } : {}),
  };
};

const sendBroadcastPush = async (input = {}) => {
  const requestedCount = toInt(input?.resolvedRecipients?.filteredCount, 0);

  try {
    const payload = buildPushBroadcastPayload(input);
    try {
      const response = await callPost(INBOX_BROADCAST_PUSH_ENDPOINT, payload);
      return normalizeBroadcastResponse({
        response,
        requestedCount,
        endpointUsed: INBOX_BROADCAST_PUSH_ENDPOINT,
      });
    } catch (primaryError) {
      if (!shouldFallbackToLegacyBroadcast(primaryError)) {
        throw primaryError;
      }
      const fallbackResponse = await callPost(INBOX_BROADCAST_ENDPOINT, payload);
      return normalizeBroadcastResponse({
        response: fallbackResponse,
        requestedCount,
        endpointUsed: INBOX_BROADCAST_ENDPOINT,
      });
    }
  } catch (error) {
    return {
      ok: false,
      state: "failure",
      summary: {
        requestedCount,
        sent: 0,
        failed: requestedCount,
        skipped: 0,
        endpointUsed: null,
      },
      error: buildNormalizedBroadcastError({ error }),
      raw: null,
    };
  }
};

export {
  listNotificationInbox,
  getNotificationInboxUnreadCount,
  markNotificationInboxAsRead,
  markAllNotificationInboxAsRead,
  createNotificationBroadcast,
  fetchBroadcastRecipientPage,
  listBroadcastAudienceCandidates,
  resolveBroadcastRecipients,
  applyClientRecipientFilters,
  broadcastPushNotification,
  sendBroadcastPush,
};
